import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import { removePointsAndParenthesis } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useMultCoefficient2 from './useMultCoefficient2'

function MultCoefficient2({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    integers,
    coefficient,
    variable,
    formValid,
    inputRef,
    answer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useMultCoefficient2({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
    answerState,
    autofocus,
  })

  // The input is one bare box for the whole term, not just the coefficient: no trailing variable.
  useEmitInstance(
    onInstanceChange,
    integers.length > 0
      ? `${integers
          .map((integer, index) => (integer < 0 && index > 0 ? `(${integer})` : `${integer}`))
          .join(' \\cdot ')} \\cdot ${coefficient < 0 ? '(-' : ''}${
          Math.abs(coefficient) !== 1 ? Math.abs(coefficient) : ''
        }${variable}${coefficient < 0 ? ')' : ''} = \\square`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row flex-wrap justify-center items-center mt-2">
          {integers.map((integer, index) => (
            <span key={integer}>
              {integer < 0 && index > 0 ? `(${integer})` : integer}
              <span className="mx-2">×</span>
            </span>
          ))}
          {coefficient < 0 && '(-'}
          {Math.abs(coefficient) !== 1 && Math.abs(coefficient)}
          <i>{variable}</i>
          {coefficient < 0 && ')'}
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertCoefficientValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={10}
              onChange={(value) => onInputChange(removePointsAndParenthesis(value))}
              onEnter={onEnterPress}
              ref={inputRef}
              type="text"
              value={answer}
            />
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange('clear'),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default MultCoefficient2
